
import React from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
];

const DonutChartWithRightLabels = ({ data }) => {
  // Remove 'Unknown' and 'unknown_service' services at the data level (case-insensitive)
  const filteredData = Array.isArray(data)
    ? data.filter(d => {
        if (!d.name) return false;
        const n = d.name.trim().toLowerCase();
        return n !== 'unknown' && n !== 'unknown_service';
      })
    : [];
  
  // Ensure values are properly parsed as numbers and create clean data structure
  const cleanData = filteredData.map(service => {
    const numericValue = parseFloat(service.value);
    return {
      name: service.name,
      value: numericValue || 0,
      formatted: service.formatted || `${numericValue.toFixed(2)} MB`
    };
  });
  
  // Data is already sorted by volume descending from DashboardPage
  // Calculate percentages for display
  const totalVolume = cleanData.reduce((sum, service) => sum + service.value, 0);
  const dataWithPercentages = cleanData.map(service => ({
    ...service,
    percentage: totalVolume > 0 ? ((service.value / totalVolume) * 100).toFixed(1) : 0
  }));
  
  // Assign a unique color to each service by sorted order (volume)
  const colorMap = dataWithPercentages.reduce((acc, entry, idx) => {
    acc[entry.name] = COLORS[idx % COLORS.length];
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%' }}>

      {/* Pie Chart - No inner radius for solid pie chart */}
      <div style={{ width: 220, height: 220, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
                         <Pie
               data={dataWithPercentages}
               cx="50%"
               cy="50%"
               outerRadius={80}
               dataKey="value"
               isAnimationActive
               labelLine={false}
               paddingAngle={2}
               nameKey="name"
               minAngle={3}
             >
                             {dataWithPercentages.map((entry) => (
                 <Cell 
                   key={entry.name} 
                   fill={colorMap[entry.name]} 
                   stroke="#ffffff"
                   strokeWidth={1}
                 />
               ))}
            </Pie>
            <Tooltip 
              formatter={(value, name) => {
                const service = dataWithPercentages.find(s => s.name === name);
                return [`${value.toFixed(2)} MB (${service?.percentage}%)`, name];
              }} 
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      
      {/* Side legend with percentages - matching the Figma design */}
      <div style={{ minWidth: 0, marginLeft: 24, flex: 1, display: 'flex', flexDirection: 'column', gap: 12, background: '#fff' }}>
        {dataWithPercentages.map((entry) => (
          <div key={entry.name} style={{ display: 'flex', alignItems: 'center', marginBottom: 4, minWidth: 0 }}>
            <div
              style={{
                width: 14,
                height: 14,
                backgroundColor: colorMap[entry.name],
                borderRadius: 3,
                marginRight: 12,
                flexShrink: 0
              }}
            ></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ 
                fontWeight: 500, 
                fontSize: 14, 
                color: '#333',
                marginBottom: 2,
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: '1.3'
              }}>
                {entry.name}
              </div>
              <div style={{ 
                fontSize: 12, 
                color: '#666',
                display: 'flex',
                gap: '8px',
                alignItems: 'center'
              }}>
                <span>{entry.percentage}%</span>
                <span style={{ color: '#999' }}>•</span>
                <span>{entry.formatted || `${parseFloat(entry.value).toFixed(2)} MB`}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DonutChartWithRightLabels;